import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Security, Lock, Shield, Person, Email, CheckCircle } from '@mui/icons-material';

const Privacy = () => {
    return (
        <>
            <Helmet>
                <title>Politique de Confidentialité - Netikash Developer Portal</title>
                <meta name="description" content="Politique de confidentialité de Netikash : protection des données, conformité RGPD, gestion des cookies et droits des utilisateurs." />
                <meta property="og:title" content="Politique de Confidentialité - Netikash" />
                <meta property="og:description" content="Découvrez comment Netikash protège vos données personnelles et respecte votre vie privée." />
                <link rel="canonical" href="https://dev.netikash.com/privacy" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-800 mb-4">
                            <Security className="mr-2" fontSize="small" />
                            <span className="text-sm font-medium">Conformité RGPD</span>
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Politique de Confidentialité</h1>
                        <p className="text-xl text-gray-600">
                            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>

                    {/* Introduction */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <p className="text-gray-700 leading-relaxed mb-4">
                            Netikash s'engage à protéger votre vie privée et vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous utilisez notre portail développeur et nos services API.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            En utilisant nos services, vous acceptez les pratiques décrites dans cette politique. Nous respectons le Règlement Général sur la Protection des Données (RGPD) et toutes les lois applicables en matière de protection des données.
                        </p>
                    </div>

                    {/* Sections */}
                    <div className="space-y-8">
                        {/* 1. Collecte des données */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <Person className="text-blue-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">1. Données que nous collectons</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Données d'identification</h3>
                                    <ul className="list-disc list-inside space-y-1 ml-4">
                                        <li>Nom et prénom</li>
                                        <li>Adresse e-mail professionnelle</li>
                                        <li>Numéro de téléphone (optionnel)</li>
                                        <li>Nom de l'organisation/entreprise</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Données techniques</h3>
                                    <ul className="list-disc list-inside space-y-1 ml-4">
                                        <li>Adresse IP</li>
                                        <li>Type de navigateur et version</li>
                                        <li>Pages visitées et temps passé sur le site</li>
                                        <li>Identifiants API (Client ID et Client Secret)</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Données d'utilisation</h3>
                                    <ul className="list-disc list-inside space-y-1 ml-4">
                                        <li>Historique des appels API</li>
                                        <li>Transactions effectuées</li>
                                        <li>Préférences de configuration</li>
                                        <li>Logs d'accès et d'erreurs</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 2. Utilisation des données */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="text-green-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">2. Comment nous utilisons vos données</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                                        <h3 className="font-semibold text-amber-900 mb-2">Fourniture des services</h3>
                                        <p className="text-amber-800 text-sm">Authentification, traitement des paiements, support technique</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <h3 className="font-semibold text-blue-900 mb-2">Amélioration des services</h3>
                                        <p className="text-blue-800 text-sm">Analyse d'utilisation, optimisation des performances</p>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                        <h3 className="font-semibold text-purple-900 mb-2">Communication</h3>
                                        <p className="text-purple-800 text-sm">Notifications importantes, mises à jour de sécurité</p>
                                    </div>
                                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                                        <h3 className="font-semibold text-indigo-900 mb-2">Sécurité</h3>
                                        <p className="text-indigo-800 text-sm">Détection de fraude, prévention des abus</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 3. Cookies et tracking */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                    <Shield className="text-amber-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">3. Cookies et technologies de suivi</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>Nous utilisons des cookies et technologies similaires pour améliorer votre expérience. Consultez notre <a href="/cookies" className="text-amber-600 hover:underline">politique des cookies</a> pour plus de détails.</p>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="font-semibold text-gray-900 mb-2">Types de cookies utilisés :</h3>
                                    <ul className="list-disc list-inside space-y-1 text-sm">
                                        <li><strong>Cookies essentiels</strong> : Nécessaires au fonctionnement du site</li>
                                        <li><strong>Cookies analytiques</strong> : Pour comprendre l'utilisation du site</li>
                                        <li><strong>Cookies de préférences</strong> : Pour mémoriser vos choix</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 4. Partage des données */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                                    <Lock className="text-red-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">4. Partage et transfert des données</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations uniquement dans les cas suivants :</p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li><strong>Prestataires de services</strong> : Partenaires de confiance qui nous aident à fournir nos services (hébergement, analyse)</li>
                                    <li><strong>Obligations légales</strong> : Lorsque requis par la loi ou pour répondre à des demandes gouvernementales</li>
                                    <li><strong>Protection de nos droits</strong> : Pour protéger nos droits, votre sécurité ou celle d'autrui</li>
                                    <li><strong>Avec votre consentement</strong> : Dans tout autre cas avec votre autorisation explicite</li>
                                </ul>
                            </div>
                        </section>

                        {/* 5. Droits des utilisateurs (RGPD) */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="text-green-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">5. Vos droits (RGPD)</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p className="mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit d'accès</h3>
                                        <p className="text-sm">Accéder à vos données personnelles</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit de rectification</h3>
                                        <p className="text-sm">Corriger vos données inexactes</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit à l'effacement</h3>
                                        <p className="text-sm">Demander la suppression de vos données</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit à la portabilité</h3>
                                        <p className="text-sm">Récupérer vos données dans un format structuré</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit d'opposition</h3>
                                        <p className="text-sm">Vous opposer au traitement de vos données</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-gray-900 mb-2">Droit à la limitation</h3>
                                        <p className="text-sm">Limiter l'utilisation de vos données</p>
                                    </div>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                                    <p className="text-blue-900 text-sm">
                                        <strong>Pour exercer vos droits</strong>, contactez notre Délégué à la Protection des Données (DPO) à l'adresse : <a href="mailto:dpo@netikash.com" className="text-blue-600 hover:underline">dpo@netikash.com</a>
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 6. Sécurité */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                                    <Security className="text-indigo-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">6. Sécurité des données</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données :</p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li>Chiffrement SSL/TLS pour toutes les communications</li>
                                    <li>Stockage sécurisé avec chiffrement au repos</li>
                                    <li>Accès restreint aux données personnelles</li>
                                    <li>Surveillance continue et détection d'intrusions</li>
                                    <li>Conformité PCI DSS Level 1 pour les données de paiement</li>
                                    <li>Audits de sécurité réguliers</li>
                                </ul>
                            </div>
                        </section>

                        {/* 7. Conservation des données */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Durée de conservation</h2>
                            <div className="space-y-4 text-gray-700">
                                <p>Nous conservons vos données personnelles uniquement aussi longtemps que nécessaire pour :</p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li>Fournir nos services et répondre à vos demandes</li>
                                    <li>Respecter nos obligations légales et réglementaires</li>
                                    <li>Résoudre les litiges et appliquer nos accords</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-4">
                                    Les données sont automatiquement supprimées après la période de conservation légale, sauf si une obligation légale nécessite une conservation plus longue.
                                </p>
                            </div>
                        </section>

                        {/* 8. Contact */}
                        <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Email className="text-amber-600" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact et DPO</h2>
                                    <div className="space-y-3 text-gray-700">
                                        <p>Pour toute question concernant cette politique de confidentialité ou vos données personnelles :</p>
                                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                                            <p className="font-semibold text-gray-900 mb-2">Délégué à la Protection des Données (DPO)</p>
                                            <p className="text-sm mb-1">
                                                <strong>Email :</strong> <a href="mailto:dpo@netikash.com" className="text-amber-600 hover:underline">dpo@netikash.com</a>
                                            </p>
                                            <p className="text-sm mb-1">
                                                <strong>Support :</strong> <a href="mailto:support@netikash.com" className="text-amber-600 hover:underline">support@netikash.com</a>
                                            </p>
                                            <p className="text-sm">
                                                <strong>Adresse :</strong> 1 Mpolo coin Kasangulu, Lubumbashi, RD Congo
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 9. Modifications */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modifications de cette politique</h2>
                            <p className="text-gray-700">
                                Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. Toute modification sera publiée sur cette page avec une date de mise à jour révisée. Nous vous encourageons à consulter régulièrement cette page pour rester informé de la façon dont nous protégeons vos données.
                            </p>
                        </section>
                    </div>

                    {/* Footer note */}
                    <div className="mt-12 text-center text-sm text-gray-500">
                        <p>Cette politique entre en vigueur le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Privacy;